/**
 * Pointer gestures of the note lane (W2-03 note-lane; DESIGN.md §5.3-5.4, §8.2 mouse table, §8.3 RPE aliases).
 *
 * The lane view (render/timeline/laneColumn.ts) hands every pointer event here with the lane geometry; this module
 * owns the state machine:
 *   - Select tool: click selects (Ctrl / Cmd toggles, Shift extends to every note between the anchor and the click,
 *     whatever its type, within the sides filter; Alt cycles the notes stacked at the same spot); dragging a note moves the selection in time and
 *     X (snapped; Shift locks the axis; Alt or Alt-free Ctrl+Shift ignores snapping; Esc cancels) as a live gesture,
 *     or as a render-time ghost transform above 300 notes (applied on release); dragging a Hold's head or tail
 *     changes its times; dragging empty space starts the host's cross-column marquee; with two or more selected notes
 *     the transform box stretches time (top / bottom edge, Ctrl: around the playhead) and scales X (side edges,
 *     Ctrl: around X 0).
 *   - Note tools: a click places (Shift+click stacks on purpose; a click on a note selects it instead); the Hold
 *     tool places by press, drag up, release. The Eraser deletes what it clicks or drags across (one step).
 *   - While a ghost paste is up a click commits it; while an R ... R Hold is open a click ends it.
 *   - Hover: the editor store's `hover` (lane point or note: the preview draws the link), the tooltip target, the
 *     placement ghost and the hint bar's sentence follow the pointer.
 *   - RPE preset aliases: Z held + drag moves notes whatever the tool (Z + right-drag on a Hold moves its end), D held
 *     + right click deletes the note under the pointer, Shift+click on empty space twice draws a marquee.
 *   - RPE preset Ctrl+wheel over a lane with notes selected fine-tunes their width (NOTE-13), as one undoable step
 *     per wheel burst; a trackpad pinch still zooms time, and without selected notes the host keeps the wheel.
 *   - Double click on a note focuses the matching inspector field; right click opens the lane's context menu.
 * Locked lines (DESIGN.md §4.10) refuse every edit with a toast: placing, moving, the Hold handles, the transform
 * box, the Eraser and the D + right-click delete; selecting still works.
 */

import { Beats } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import type { KeyId, LineId, NoteId } from '../../core/model/types';
import { Core } from '../../core/commands/index';
import type { AppServices } from '../../app/registry/types';
import type { Gesture, HoverTarget } from '../../state/types';
import type { LaneHandlers, LanePointer, LaneView } from '../../render/timeline/laneColumn';
import { notesBetween, stackAt } from '../../render/timeline/laneHit';
import { m } from './noteLane.messages';
import {
  hostOf,
  isLockedLine,
  itemsText,
  laneSession,
  noSnapModifier,
  playheadBeat,
  ref,
  selectedItems,
  snapBeatAt,
  snapStepBeat,
  snapXAt,
  touchesLockedLine,
  type LaneSession,
  gestureHints,
} from './laneSession';
import { finishHold, holdSpan, placeAllowed, placeNote, toolKind, updateToolHints } from './placement';
import { commitGhost } from './pasteFlow';
import { createWheelStepper, fineTuneWidth, isPinch, wheelDeltaPx } from './noteTune';
import { noteBox, scaleXSpec, stretchSpec, type NoteBox } from './transformBox';

/** Above this many notes a drag moves a render-time ghost and applies the move once on release. */
export const GHOST_DRAG_LIMIT = 300;
/** Pointer travel (px) before a press on a note becomes a drag. */
const DRAG_PX = 3;
/** Pointer travel (px) with Shift held that fixes the locked axis of a note drag (DESIGN.md §4.7). */
const AXIS_LOCK_PX = 4;

type Press =
  | {
      readonly kind: 'move';
      readonly view: LaneView;
      readonly ids: readonly NoteId[];
      readonly keys: readonly KeyId[];
      readonly grabT: Beat;
      readonly grabX: number;
      readonly minT: Beat;
      readonly startBeat: number;
      readonly startXRpe: number;
      readonly startPx: number;
      readonly startPy: number;
      readonly gesture: Gesture | null;
      readonly clicked: NoteId;
      moved: boolean;
      dt: Beat;
      dx: number;
      /**
       * The Shift axis lock, fixed by the first AXIS_LOCK_PX of travel with Shift held ('time': vertical, X kept;
       * 'x': horizontal, time kept) and kept while Shift stays down; releasing Shift frees the drag again.
       */
      axis: 'time' | 'x' | null;
    }
  | {
      readonly kind: 'holdEdge';
      readonly view: LaneView;
      readonly part: 'head' | 'tail';
      readonly grabbed: NoteId;
      readonly holds: ReadonlyMap<NoteId, { readonly t: Beat; readonly end: Beat }>;
      readonly gesture: Gesture;
      delta: Beat;
    }
  | { readonly kind: 'holdDraw'; readonly view: LaneView; readonly line: LineId; readonly free: boolean }
  | { readonly kind: 'eraser'; readonly view: LaneView; readonly ids: Set<NoteId>; lastPx: number; lastPy: number }
  | {
      readonly kind: 'box';
      readonly view: LaneView;
      readonly handle: 'top' | 'bottom' | 'left' | 'right';
      readonly box: NoteBox;
      readonly notes: readonly NoteId[];
      readonly keys: readonly KeyId[];
      readonly gesture: Gesture;
      changed: boolean;
    }
  /** A press on a note of a locked line: selection works, a drag explains why nothing moves. */
  | { readonly kind: 'locked'; readonly startPx: number; readonly startPy: number; warned: boolean }
  | { readonly kind: 'none' };

const NONE: Press = { kind: 'none' };

/** Selection helpers over the editor store. */
function selectNotes(
  services: AppServices,
  ids: readonly NoteId[],
  mode: 'replace' | 'add' | 'toggle' | 'subtract',
  primary: NoteId | null,
): void {
  const e = services.editor.getState();
  e.select(
    {
      notes: new Set(ids),
      ...(primary === null
        ? {}
        : { primary: { kind: 'note' as const, id: primary }, anchor: { kind: 'note' as const, id: primary } }),
    },
    mode,
  );
}

function sameHover(a: HoverTarget | null, b: HoverTarget | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind === 'note' && b.kind === 'note') return a.note === b.note;
  if (a.kind === 'lanePoint' && b.kind === 'lanePoint') return a.line === b.line && a.x === b.x && a.beat === b.beat;
  return false;
}

/** Selected keys plus both keys of every selected segment (time operations move them with the notes). */
function selectedKeysForTime(services: AppServices): KeyId[] {
  const store = services.store;
  if (store === null) return [];
  const sel = selectedItems(services);
  const out = new Set<KeyId>(sel.keys);
  for (const id of sel.segments) {
    out.add(id);
    const next = store.idx.nextKey(id);
    if (next !== undefined) out.add(next);
  }
  return [...out];
}

export interface LaneGestureController extends LaneHandlers {
  /** The press in progress (tests, diagnostics). */
  readonly pressKind: Press['kind'];
}

export function createLaneHandlers(services: AppServices): LaneGestureController {
  const session: LaneSession = laneSession(services);
  let press: Press = NONE;
  /** RPE Shift-click-click marquee: the first corner (lane instance, beat, content px). */
  let rpeAnchor: { readonly view: LaneView; readonly beat: number; readonly x: number } | null = null;
  /** Suppress the context menu that follows a D + right-click delete. */
  let suppressMenu = false;
  /** Last time a wheel fine-tune was refused with a toast (one explanation per wheel burst). */
  let wheelRefusedAt = Number.NEGATIVE_INFINITY;
  const widthSteps = createWheelStepper();

  const snapState = () => services.editor.getState().snap;

  function setPointer(view: LaneView, e: LanePointer | null): void {
    if (e === null) {
      if (session.pointer?.instanceId === view.instanceId) session.pointer = null;
      return;
    }
    session.pointer = {
      instanceId: view.instanceId,
      line: view.model.line(),
      beat: e.beat,
      x: e.xRpe,
      px: e.x,
      py: e.y,
      alt: noSnapModifier(services, e),
      ctrl: e.ctrl,
      shift: e.shift,
    };
    session.lastX = e.xRpe;
  }

  function setHover(target: HoverTarget | null): void {
    const e = services.editor.getState();
    if (!sameHover(e.hover, target)) e.setHover(target);
  }

  function refuseLocked(line: LineId | null): boolean {
    if (!isLockedLine(services, line)) return false;
    services.ui.toast(ref(m.reasonLocked), { kind: 'warning' });
    return true;
  }

  // ---- selection clicks ----

  function clickNote(view: LaneView, e: LanePointer, id: NoteId): 'drag' | 'done' {
    const sel = services.editor.getState().selection;
    const index = view.model.notes();
    if (e.alt && e.hit !== null && e.hit.stack > 1) {
      // Cycle through the notes at the same spot.
      const stack = stackAt(index, e.hit.index);
      const cur = stack.findIndex((i) => sel.notes.has((index.ids[i] ?? -1) as NoteId));
      const next = stack[(cur + 1) % stack.length] ?? e.hit.index;
      const nextId = (index.ids[next] ?? id) as NoteId;
      selectNotes(services, [nextId], 'replace', nextId);
      return 'done';
    }
    if (e.ctrl) {
      selectNotes(services, [id], 'toggle', sel.notes.has(id) ? null : id);
      return 'done';
    }
    if (e.shift) {
      const anchor = sel.anchor;
      const ai = anchor?.kind === 'note' ? index.indexOf(anchor.id as NoteId) : -1;
      const ci = index.indexOf(id);
      if (ai >= 0 && ci >= 0) {
        // DESIGN.md §5.4 "everything of the same kind": the selection kind (notes, not keyframes), so every note of
        // the lane between the anchor and the click, whatever its type; only the sides filter narrows it.
        const include = (i: number): boolean => view.model.include?.(i) ?? true;
        const ids = notesBetween(index, index.t[ai] ?? 0, index.t[ci] ?? 0, include);
        services.editor.getState().select({ notes: new Set(ids), primary: { kind: 'note', id } }, 'add');
        return 'done';
      }
      selectNotes(services, [id], 'add', id);
      return 'done';
    }
    if (!sel.notes.has(id)) selectNotes(services, [id], 'replace', id);
    // Already selected: keep the selection (it may be dragged); the clicked note becomes the primary item and the
    // anchor of the next Shift+click.
    else services.editor.getState().select({ primary: { kind: 'note', id }, anchor: { kind: 'note', id } }, 'add');
    return 'drag';
  }

  // ---- move ----

  function startMove(view: LaneView, e: LanePointer, clicked: NoteId): void {
    const store = services.store;
    if (store === null) return;
    const sel = selectedItems(services);
    const ids = sel.notes.includes(clicked) ? sel.notes : [clicked];
    const grab = store.doc.notes.get(clicked);
    if (grab === undefined) return;
    let minT = grab.t;
    for (const id of ids) {
      const n = store.doc.notes.get(id);
      if (n !== undefined && Beats.lt(n.t, minT)) minT = n.t;
    }
    const keys = sel.notes.includes(clicked) ? selectedKeysForTime(services) : [];
    // The selection may reach into other (pinned) lanes and motion columns: any locked line refuses the drag.
    if (touchesLockedLine(services, { notes: ids, keys })) {
      press = { kind: 'locked', startPx: e.x, startPy: e.y, warned: false };
      return;
    }
    press = {
      kind: 'move',
      view,
      ids,
      keys,
      grabT: grab.t,
      grabX: grab.x,
      minT,
      startBeat: e.beat,
      startXRpe: e.xRpe,
      startPx: e.x,
      startPy: e.y,
      gesture: null,
      clicked,
      moved: false,
      dt: Beats.ZERO,
      dx: 0,
      axis: null,
    };
  }

  function moveDelta(p: Extract<Press, { kind: 'move' }>, e: LanePointer): { dt: Beat; dx: number } {
    const snap = snapState();
    const free = noSnapModifier(services, e);
    const newT = snapBeatAt(Beats.toF64(p.grabT) + (e.beat - p.startBeat), snap, free);
    let dt = Beats.sub(newT, p.grabT);
    if (Beats.lt(Beats.add(p.minT, dt), Beats.ZERO)) dt = Beats.sub(Beats.ZERO, p.minT);
    let dx = snapXAt(p.grabX + (e.xRpe - p.startXRpe), snap, free) - p.grabX;
    dx = Math.round(dx * 100) / 100;
    if (!e.shift) p.axis = null;
    else {
      const ax = Math.abs(e.x - p.startPx);
      const ay = Math.abs(e.y - p.startPy);
      const lean = ay >= ax ? 'time' : 'x';
      // Fixed once the pointer has travelled far enough, so a diagonal drag never flips axes mid-gesture.
      if (p.axis === null && Math.max(ax, ay) >= AXIS_LOCK_PX) p.axis = lean;
      if ((p.axis ?? lean) === 'time') dx = 0;
      else dt = Beats.ZERO;
    }
    return { dt, dx };
  }

  function applyMove(p: Extract<Press, { kind: 'move' }>, dt: Beat, dx: number): void {
    const store = services.store;
    if (store === null) return;
    if (Beats.eq(dt, p.dt) && dx === p.dx) return;
    p.dt = dt;
    p.dx = dx;
    if (p.ids.length > GHOST_DRAG_LIMIT) {
      const wasIdle = session.gesture.move === null;
      session.gesture.move = { ids: new Set(p.ids), dBeat: Beats.toF64(dt), dx };
      session.redraw(wasIdle ? ['content', 'overlay'] : ['overlay']);
      return;
    }
    let g = p.gesture;
    if (g === null) {
      const count = p.ids.length + p.keys.length;
      g = store.beginGesture({
        label: ref(p.keys.length > 0 ? m.txMoveItems : m.txMove, { count: p.keys.length > 0 ? count : p.ids.length }),
        command: 'notes.move',
      });
      press = { ...p, gesture: g };
    }
    const zero = Beats.eq(dt, Beats.ZERO) && dx === 0;
    g.update((tx) => {
      if (zero) return;
      if (p.keys.length > 0) Core.transform.transform(tx, { notes: p.ids, keys: p.keys, spec: { dt, dx } });
      else Core.notes.moveNotes(tx, { ids: p.ids, dt, dx, snapDiv: services.editor.getState().snap.div });
    });
  }

  function finishMove(p: Extract<Press, { kind: 'move' }>): void {
    const store = services.store;
    gestureHints(services, null);
    if (session.gesture.move !== null) {
      session.gesture.move = null;
      session.redraw(['content', 'overlay']);
      if (store === null || (Beats.eq(p.dt, Beats.ZERO) && p.dx === 0)) return;
      const out = store.transact({ label: ref(m.txMove, { count: p.ids.length }), command: 'notes.move' }, (tx) => {
        if (p.keys.length > 0)
          Core.transform.transform(tx, { notes: p.ids, keys: p.keys, spec: { dt: p.dt, dx: p.dx } });
        else Core.notes.moveNotes(tx, { ids: p.ids, dt: p.dt, dx: p.dx, snapDiv: services.editor.getState().snap.div });
      });
      if (!out.ok) services.ui.toast(out.reason, { kind: 'error' });
      return;
    }
    const g = p.gesture;
    if (g === null) return;
    if (Beats.eq(p.dt, Beats.ZERO) && p.dx === 0) g.cancel();
    else g.commit();
  }

  // ---- hold edges ----

  function startHoldEdge(view: LaneView, id: NoteId, part: 'head' | 'tail'): void {
    const store = services.store;
    if (store === null) return;
    const sel = services.editor.getState().selection;
    const ids = sel.notes.has(id) ? [...sel.notes] : [id];
    if (!sel.notes.has(id)) selectNotes(services, [id], 'replace', id);
    const holds = new Map<NoteId, { t: Beat; end: Beat }>();
    for (const nid of ids) {
      const n = store.doc.notes.get(nid);
      if (n?.kind === 'hold') holds.set(nid, { t: n.t, end: n.end });
    }
    if (!holds.has(id)) return;
    if (touchesLockedLine(services, { notes: holds.keys() })) {
      services.ui.toast(ref(m.reasonLocked), { kind: 'warning' });
      return;
    }
    const gesture = store.beginGesture({
      label: ref(m.txHoldLength, { count: holds.size }),
      command: 'notes.holdLength',
    });
    press = { kind: 'holdEdge', view, part, grabbed: id, holds, gesture, delta: Beats.ZERO };
  }

  function dragHoldEdge(p: Extract<Press, { kind: 'holdEdge' }>, e: LanePointer): void {
    const snap = snapState();
    const free = noSnapModifier(services, e);
    const g = p.holds.get(p.grabbed);
    if (g === undefined) return;
    const target = snapBeatAt(e.beat, snap, free);
    const delta = Beats.sub(target, p.part === 'head' ? g.t : g.end);
    if (Beats.eq(delta, p.delta)) return;
    p.delta = delta;
    const step = free ? Beats.make(0, 1, 1920) : snapStepBeat(snap);
    p.gesture.update((tx) => {
      if (Beats.eq(delta, Beats.ZERO)) return;
      for (const [id, h] of p.holds) {
        if (p.part === 'tail') {
          let end = Beats.add(h.end, delta);
          const minEnd = Beats.add(h.t, step);
          if (Beats.lt(end, minEnd)) end = minEnd;
          if (!Beats.eq(end, h.end)) Core.notes.setNoteTimes(tx, { id, end });
        } else {
          let t = Beats.add(h.t, delta);
          const maxT = Beats.sub(h.end, step);
          if (Beats.lt(maxT, t)) t = maxT;
          if (Beats.lt(t, Beats.ZERO)) t = Beats.ZERO;
          if (!Beats.eq(t, h.t)) Core.notes.setNoteTimes(tx, { id, t, snapDiv: services.editor.getState().snap.div });
        }
      }
    });
  }

  // ---- transform box ----

  function startBox(view: LaneView, handle: 'top' | 'bottom' | 'left' | 'right'): boolean {
    const store = services.store;
    if (store === null) return false;
    const sel = services.editor.getState().selection;
    const index = view.model.notes();
    const box = noteBox(index, sel.notes, (id) => {
      const n = store.doc.notes.get(id);
      return n === undefined ? null : { t: n.t, end: n.end };
    });
    if (box === null) return false;
    const notes = [...sel.notes].filter((id) => index.indexOf(id) >= 0);
    const keys = handle === 'top' || handle === 'bottom' ? selectedKeysForTime(services) : [];
    if (touchesLockedLine(services, { notes, keys })) {
      services.ui.toast(ref(m.reasonLocked), { kind: 'warning' });
      return true;
    }
    const count = notes.length + keys.length;
    const gesture = store.beginGesture({
      label: ref(handle === 'top' || handle === 'bottom' ? m.txStretch : m.txScaleX, { count }),
      command: 'notes.transformBox',
    });
    press = { kind: 'box', view, handle, box, notes, keys, gesture, changed: false };
    session.gesture.boxHandle = handle;
    gestureHints(services, [
      {
        chord: 'Ctrl',
        label: ref(handle === 'top' || handle === 'bottom' ? m.gestureAroundPlayhead : m.gestureAroundZero),
      },
      { chord: 'Alt', label: ref(m.gestureNoSnap) },
      { chord: 'Escape', label: ref(m.gestureCancel) },
    ]);
    return true;
  }

  function dragBox(p: Extract<Press, { kind: 'box' }>, e: LanePointer): void {
    const snap = snapState();
    const free = noSnapModifier(services, e);
    let spec;
    if (p.handle === 'top' || p.handle === 'bottom') {
      const edge = snapBeatAt(e.beat, snap, free);
      const pivot = e.ctrl ? snapBeatAt(playheadBeat(services), snap, false) : null;
      spec = stretchSpec(p.box, p.handle, edge, pivot);
    } else {
      spec = scaleXSpec(p.box, p.handle, snapXAt(e.xRpe, snap, free), e.ctrl);
    }
    p.changed = spec !== null;
    p.gesture.update((tx) => {
      if (spec !== null) Core.transform.transform(tx, { notes: p.notes, keys: p.keys, spec });
    });
    session.redraw(['overlay']);
  }

  // ---- eraser ----

  function eraseAt(p: Extract<Press, { kind: 'eraser' }>, e: LanePointer): void {
    const axis = p.view.axis;
    if (axis === null) return;
    const steps = Math.max(1, Math.ceil(Math.hypot(e.x - p.lastPx, e.y - p.lastPy) / 3));
    for (let k = 1; k <= steps; k++) {
      const x = p.lastPx + ((e.x - p.lastPx) * k) / steps;
      const y = p.lastPy + ((e.y - p.lastPy) * k) / steps;
      const hit = p.view.noteAt(x, y, axis);
      if (hit !== null) p.ids.add(hit.id);
    }
    p.lastPx = e.x;
    p.lastPy = e.y;
    const trail = session.gesture.eraser ?? [];
    trail.push([e.x, e.y]);
    session.gesture.eraser = trail;
    session.redraw(['overlay']);
  }

  function finishEraser(p: Extract<Press, { kind: 'eraser' }>): void {
    session.gesture.eraser = null;
    session.redraw(['overlay']);
    const store = services.store;
    if (store === null || p.ids.size === 0) return;
    // A line locked while the eraser was down keeps its notes.
    const ids = [...p.ids].filter((id) => !isLockedLine(services, store.doc.notes.get(id)?.line ?? null));
    if (ids.length === 0) {
      services.ui.toast(ref(m.reasonLocked), { kind: 'warning' });
      return;
    }
    const out = store.transact({ label: ref(m.txErase, { count: ids.length }), command: 'tools.eraser' }, (tx) => {
      Core.notes.deleteNotes(tx, { ids });
      tx.setSelection({ notes: [] });
    });
    if (!out.ok) services.ui.toast(out.reason, { kind: 'error' });
    else
      services.ui.toast(ref(m.toastErased, { items: itemsText(services, { notes: ids.length }) }), {
        kind: 'info',
        undo: true,
      });
  }

  // ---- handlers ----

  function deleteAt(e: LanePointer): void {
    const store = services.store;
    if (store === null || e.hit === null) return;
    const id = e.hit.id;
    if (refuseLocked(store.doc.notes.get(id)?.line ?? null)) return;
    const out = store.transact(
      { label: ref(m.txDelete, { items: itemsText(services, { notes: 1 }) }), command: 'edit.delete' },
      (tx) => {
        Core.notes.deleteNotes(tx, { ids: [id] });
      },
    );
    if (!out.ok) services.ui.toast(out.reason, { kind: 'error' });
  }

  const handlers: LaneGestureController = {
    get pressKind() {
      return press.kind;
    },

    down(view, e) {
      press = NONE;
      setPointer(view, e);
      if (session.tooltip !== null) {
        session.tooltip = null;
        session.emit();
      }
      const store = services.store;
      if (store === null) return;
      const line = view.model.line();
      const rpe = services.keymap.preset === 'rpe';
      // Right button: only the RPE aliases (the context menu follows otherwise).
      if ((e.buttons & 2) !== 0) {
        if (rpe && session.heldD && e.hit !== null) {
          suppressMenu = true;
          if (!refuseLocked(line)) deleteAt(e);
          return;
        }
        if (rpe && session.heldZ && e.hit !== null && !refuseLocked(line)) {
          const n = store.doc.notes.get(e.hit.id);
          if (n?.kind === 'hold') {
            suppressMenu = true;
            startHoldEdge(view, e.hit.id, 'tail');
          }
        }
        return;
      }
      if ((e.buttons & 1) === 0) return;
      const ed = services.editor.getState();
      // A ghost paste or an open R ... R Hold takes the click.
      if (ed.placement?.kind === 'paste') {
        commitGhost(services);
        return;
      }
      if (ed.placement?.kind === 'hold') {
        finishHold(services, ed.placement, { beat: e.beat, free: noSnapModifier(services, e) }, 'notes.hold');
        return;
      }
      if (line === null) return;
      const tool = rpe && session.heldZ ? 'select' : ed.tool;
      const free = noSnapModifier(services, e);
      // RPE Shift+click ... Shift+click marquee on empty space.
      if (rpe && e.shift && e.hit === null && (e.handle === null || e.handle === 'body') && tool === 'select') {
        if (rpeAnchor === null || rpeAnchor.view !== view) {
          rpeAnchor = { view, beat: e.beat, x: e.x };
          return;
        }
        const host = hostOf(session);
        const a = rpeAnchor;
        rpeAnchor = null;
        const layout = host?.getLayout().find((l) => l.id === view.instanceId);
        const ox = layout?.x ?? 0;
        const items = host?.queryRect(a.beat, e.beat, ox + a.x, ox + e.x) ?? {
          notes: [],
          keys: [],
          segments: [],
          columns: [],
        };
        ed.select({ notes: new Set(items.notes), keys: new Set(items.keys), segments: new Set(items.segments) }, 'add');
        return;
      }
      rpeAnchor = null;
      if (tool === 'eraser') {
        if (refuseLocked(line)) return;
        press = { kind: 'eraser', view, ids: new Set(e.hit === null ? [] : [e.hit.id]), lastPx: e.x, lastPy: e.y };
        session.gesture.eraser = [[e.x, e.y]];
        session.redraw(['overlay']);
        return;
      }
      const kind = toolKind(tool);
      if (kind !== null && (e.hit === null || e.shift)) {
        if (refuseLocked(line)) return;
        const allowed = placeAllowed(services, line);
        if (allowed !== true) {
          services.ui.toast(allowed, { kind: 'warning' });
          return;
        }
        const snap = snapState();
        if (kind === 'hold') {
          const start = Beats.toF64(snapBeatAt(e.beat, snap, free));
          session.gesture.holdDraw = { line, start, end: start, x: snapXAt(e.xRpe, snap, free) };
          press = { kind: 'holdDraw', view, line, free };
          gestureHints(services, [
            { chord: 'Escape', label: ref(m.gestureCancel) },
            { chord: 'Alt', label: ref(m.gestureNoSnap) },
          ]);
          session.redraw(['overlay']);
          return;
        }
        placeNote(services, {
          line,
          kind,
          t: snapBeatAt(e.beat, snap, free),
          x: snapXAt(e.xRpe, snap, free),
          command: `tools.${kind}`,
        });
        return;
      }
      // Transform box edges (select tool, 2+ notes).
      if (tool === 'select' && e.handle !== null && e.handle !== 'body' && e.hit === null) {
        if (refuseLocked(line)) return;
        if (startBox(view, e.handle)) return;
      }
      if (e.hit !== null) {
        const id = e.hit.id;
        if ((e.hit.part === 'head' || e.hit.part === 'tail') && !e.ctrl && !e.shift && !e.alt) {
          if (refuseLocked(line)) return;
          startHoldEdge(view, id, e.hit.part);
          return;
        }
        if (clickNote(view, e, id) !== 'drag') return;
        if (isLockedLine(services, line)) press = { kind: 'locked', startPx: e.x, startPy: e.y, warned: false };
        else startMove(view, e, id);
        return;
      }
      if (tool === 'select' && e.handle === 'body') {
        // Inside the box but not on a note: drag the selection by its first note.
        const first = selectedItems(services).notes.find((nid) => view.model.notes().indexOf(nid) >= 0);
        if (first !== undefined && !isLockedLine(services, line)) {
          startMove(view, e, first);
          return;
        }
      }
      // Empty space: the host's marquee across columns (a click clears the selection).
      hostOf(session)?.capturePointer(view.instanceId);
    },

    drag(view, e) {
      setPointer(view, e);
      const p = press;
      switch (p.kind) {
        case 'move': {
          if (!p.moved) {
            if (Math.hypot(e.x - p.startPx, e.y - p.startPy) < DRAG_PX) return;
            p.moved = true;
            gestureHints(services, [
              { chord: 'Shift', label: ref(m.gestureAxisLock) },
              { chord: 'Alt', label: ref(m.gestureNoSnap) },
              { chord: 'Escape', label: ref(m.gestureCancel) },
            ]);
          }
          const { dt, dx } = moveDelta(p, e);
          applyMove(p, dt, dx);
          return;
        }
        case 'holdEdge':
          dragHoldEdge(p, e);
          return;
        case 'holdDraw': {
          const d = session.gesture.holdDraw;
          if (d === null) return;
          const end = Beats.toF64(snapBeatAt(e.beat, snapState(), noSnapModifier(services, e)));
          if (end !== d.end) {
            session.gesture.holdDraw = { ...d, end };
            session.redraw(['overlay']);
          }
          return;
        }
        case 'eraser':
          eraseAt(p, e);
          return;
        case 'box':
          dragBox(p, e);
          return;
        case 'locked':
          if (!p.warned && Math.hypot(e.x - p.startPx, e.y - p.startPy) >= DRAG_PX) {
            p.warned = true;
            services.ui.toast(ref(m.reasonLocked), { kind: 'warning' });
          }
          return;
        case 'none':
          return;
      }
    },

    up(view, e) {
      setPointer(view, e);
      const p = press;
      press = NONE;
      switch (p.kind) {
        case 'move':
          if (p.moved) finishMove(p);
          else {
            // A plain click on a note of a multi-selection keeps only that note.
            const sel = services.editor.getState().selection;
            if (!e.ctrl && !e.shift && !e.alt && sel.notes.size > 1)
              selectNotes(services, [p.clicked], 'replace', p.clicked);
          }
          return;
        case 'holdEdge':
          gestureHints(services, null);
          if (Beats.eq(p.delta, Beats.ZERO)) p.gesture.cancel();
          else p.gesture.commit();
          return;
        case 'holdDraw': {
          const d = session.gesture.holdDraw;
          session.gesture.holdDraw = null;
          gestureHints(services, null);
          session.redraw(['overlay']);
          if (d === null) return;
          const snap = snapState();
          const a = snapBeatAt(Math.min(d.start, d.end), snap, p.free);
          const b = snapBeatAt(Math.max(d.start, d.end), snap, p.free);
          const span = holdSpan(a, b, snap);
          placeNote(services, { line: p.line, kind: 'hold', t: span.t, end: span.end, x: d.x, command: 'tools.hold' });
          return;
        }
        case 'eraser':
          eraseAt(p, e);
          finishEraser(p);
          return;
        case 'box':
          session.gesture.boxHandle = null;
          gestureHints(services, null);
          if (p.changed) p.gesture.commit();
          else p.gesture.cancel();
          session.redraw(['overlay']);
          return;
        case 'locked':
        case 'none':
          return;
      }
    },

    cancel(_view) {
      const p = press;
      press = NONE;
      gestureHints(services, null);
      switch (p.kind) {
        case 'move':
          if (session.gesture.move !== null) {
            session.gesture.move = null;
            session.redraw(['content', 'overlay']);
          }
          p.gesture?.cancel();
          return;
        case 'holdEdge':
          p.gesture.cancel();
          return;
        case 'holdDraw':
          session.gesture.holdDraw = null;
          session.redraw(['overlay']);
          return;
        case 'eraser':
          session.gesture.eraser = null;
          session.redraw(['overlay']);
          return;
        case 'box':
          session.gesture.boxHandle = null;
          p.gesture.cancel();
          session.redraw(['overlay']);
          return;
        case 'locked':
        case 'none':
          return;
      }
    },

    hover(view, e) {
      if (e === null) {
        setPointer(view, null);
        setHover(null);
        session.tooltip = null;
        session.emit();
        updateToolHints(services, null, false);
        session.gesture.boxHandle = null;
        session.redraw(['overlay']);
        return;
      }
      setPointer(view, e);
      const line = view.model.line();
      if (e.hit !== null) setHover({ kind: 'note', note: e.hit.id });
      else if (line !== null) setHover({ kind: 'lanePoint', line, x: e.xRpe, beat: e.beat });
      else setHover(null);
      const prevTip = session.tooltip?.note ?? null;
      const nextTip = e.hit?.id ?? null;
      if (prevTip !== nextTip) {
        session.tooltip =
          nextTip === null || session.client === null
            ? null
            : { note: nextTip, clientX: session.client.x, clientY: session.client.y };
        session.emit();
      }
      const handle = e.handle !== null && e.handle !== 'body' ? e.handle : null;
      session.gesture.boxHandle = handle;
      updateToolHints(services, session.pointer, e.hit !== null);
      session.redraw(['overlay']);
    },

    wheel(_view, e) {
      // RPE preset only: in Default, Ctrl+wheel zooms time (the host's).
      // A drag in progress keeps the wheel scrolling (the host's).
      const store = services.store;
      if (store === null || store.gestureActive || services.keymap.preset !== 'rpe') return false;
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey || isPinch(e)) return false;
      const ids = selectedItems(services).notes;
      const dy = wheelDeltaPx(e);
      // A horizontal gesture pans the lane X (the host's).
      if (ids.length === 0 || dy === 0) return false;
      const now = typeof performance === 'undefined' ? Date.now() : performance.now();
      const steps = widthSteps(dy, now);
      if (steps === 0) return true;
      if (touchesLockedLine(services, { notes: ids })) {
        if (now - wheelRefusedAt > 1500) services.ui.toast(ref(m.reasonLocked), { kind: 'warning' });
        wheelRefusedAt = now;
        return true;
      }
      fineTuneWidth(services, steps, ids);
      return true;
    },

    doubleClick(_view, e) {
      if (e.hit === null) return;
      const id = e.hit.id;
      selectNotes(services, [id], 'replace', id);
      const field = e.hit.part === 'head' ? 'note.t' : e.hit.part === 'tail' ? 'note.end' : 'note.x';
      void services.registry.run('inspector.focusField', { field });
    },

    contextMenu(view, e) {
      if (suppressMenu) {
        suppressMenu = false;
        return;
      }
      const line = view.model.line();
      session.context = { line, beat: e.beat, x: e.xRpe, note: e.hit?.id ?? null };
      const ed = services.editor.getState();
      if (e.hit !== null && !ed.selection.notes.has(e.hit.id)) selectNotes(services, [e.hit.id], 'replace', e.hit.id);
      openLaneMenu(services, 'lane.context');
    },
  };
  return handlers;
}

/** Opens a registry menu at the last right click inside the timeline (UiService.openContextMenu). */
export function openLaneMenu(services: AppServices, menu: 'lane.context' | 'timing.context'): boolean {
  const s = laneSession(services);
  const ev = s.lastContextEvent;
  const ui = services.ui;
  if (ui.openContextMenu === undefined) return false;
  const x = ev?.clientX ?? s.client?.x ?? 0;
  const y = ev?.clientY ?? s.client?.y ?? 0;
  ui.openContextMenu(menu, x, y, triggerEvent(ev));
  return true;
}

/**
 * The right click as a command trigger event (CommandContext.event is a Keyboard-, Pointer- or WheelEvent): browsers
 * that fire `contextmenu` as a PointerEvent pass it as is, others get an equivalent PointerEvent.
 */
export function triggerEvent(ev: MouseEvent | null): PointerEvent | null {
  if (ev === null || typeof PointerEvent !== 'function') return null;
  if (ev instanceof PointerEvent) return ev;
  const pe = new PointerEvent('contextmenu', {
    clientX: ev.clientX,
    clientY: ev.clientY,
    screenX: ev.screenX,
    screenY: ev.screenY,
    button: ev.button,
    buttons: ev.buttons,
    ctrlKey: ev.ctrlKey,
    shiftKey: ev.shiftKey,
    altKey: ev.altKey,
    metaKey: ev.metaKey,
    pointerType: 'mouse',
    isPrimary: true,
  });
  Object.defineProperty(pe, 'target', { value: ev.target, enumerable: true });
  return pe;
}
